import { gql } from '@apollo/client'

export const CP_FRAGMENT = gql`
  fragment CPFragment on CP {
    cPIssuanceId
    status
    security
    price
    tradeDate
    settleDate
    cusip
    discountRate
    tenor
    mMYD
    counterpartyName
    issuerName
    notionalQty
    proceeds
    maturityDate
    cPDiscountAmount
    cpPair {
      cpLeft
      cpRight
    }
    notionalQtyInUse
    notionalQtyReminder
    programExpenseDefault
    programExpenseDefaultPct
    discountRateCalc
  }
`

export const NOTE_FRAGMENT = gql`
  fragment NoteFragment on Note {
    by
    text
  }
`

export const BLOTTER_FRAGMENT = gql`
  fragment BlotterFragment on UploadRecord {
    dealer
    tradeDate
    settleDate
    structureMtyDate
    cusip
    notional
    cleanPrice
    priceAccrued
    hairCut
    dirtyPrice
    repoRate
    tRSFixedRate
    inflationFactor
    secType
    side
  }
`

export const ASSET_FRAGMENT = gql`
  fragment AssetFragment on AssetData {
    Dealer
    TradeDate
    SettleDate
    Cusip
    Notional
    CleanPrice
    PriceAccrued
    DirtyPrice
    MarketValue
    SectionType
  }
`

export const FUNDING_RECORD_FRAGMENT = gql`
  fragment FundingRecordFragment on FundingRecord {
    issuer
    bankName
    fundingDate
    maturityDate
    tenorDays
    faceAmount
    proceeds
    discountRateTotal
    discountAmount
    moodysRating
    sAndPRating
    companyName
    principal
    interestRate
    interestAmount
    agreement
    amountDueAtMaturity
  }
`

export const FUNDING_REQUEST_TEST_FRAGMENT = gql`
  fragment FundingRequestTestFragment on FundingRequestTestResult {
    assetCashFlowSufficiencyTest
    approvedTransactionAgreementTest
    transactionCptyRatingTest
    transactionCptyMinRating
    transactionCptyRatingsOverride
    assetCoRepTest
    opGuidelineAssetSpecificReqComplTest
    notionalTest
    capitalAdequecyTest
    assetPassFailOverallTest
  }
`

export const FUNDING_REQUEST_SUM_FRAGMENT = gql`
  fragment FundingRequestSumFragment on FundingRequestSummaryData {
    fundingRequestCode
    fundingRequestADCId
    cpIssuancesId
    timeSubmitted
    submittedBy
    portfolioId
    issuerName
    counterpartyName
    assetStatus
    loanStatus
  }
`

export const FUNDING_REQUEST_FRAGMENT = gql`
  fragment FundingRequestFragment on FundingRequest {
    designatedCash
    fundingRequestCode
    cpIssuances {
      ...CPFragment
    }
    fundingRecords {
      ...FundingRecordFragment
    }
    fundingRequestTest{
      ...FundingRequestTestFragment
    }
    notes {
      ...NoteFragment
    }
  }
  ${CP_FRAGMENT}
  ${NOTE_FRAGMENT}
  ${FUNDING_RECORD_FRAGMENT}
  ${FUNDING_REQUEST_TEST_FRAGMENT}
`

export const RAW_ASSET_SUM_FRAGMENT = gql`
  fragment RawAssetSumFragment on AssetSums {
    AssetType
    Name
    BatchFileName
    BatchUniqueId
    BatchId
    MarketDataComplete
    OmsDataComplete
    RiskCalcComplete
    ClientAddressEmail
    CreatedDate
    BasketOverall
    ApprovalStatus
    FundingRequestId
    FundingRequestCode
    CounterParty
    AssetsDisable
    TotalEstimatedNotional
    PortfolioCurrency
    TotalAssetCount
    TotalQtyFI
    TotalQtyEqty
    ReconcileIndicator
    ReconcileCount
    TransactionType
    PortfolioId
    ExecScheme
  }
`

export const RAW_ASSET_DETAILS_SUMMARY_FRAGMENT = gql`
  fragment RawAssetDetailsSummaryFragment on AssetDetailsSummaryData {
    Counterparty
    CreatedDate
    TradeDate
    EffectiveDate
    TrsFinancingRate
    TrsFinancingRateType
    ScheduledMaturityDate
    FinalSettlementDate
    LiquidityCptyTotal
    ProjectedInitialMargin
    CheckTradeDate
    CheckEffectiveDate
    CheckScheduledMaturityDate
    CheckFinalSettlementDate
    MainAccountAllocation
    EarlyTerminationDate
    CheckEarlyTerminationDate
    MarketDataComplete
    OmsDataComplete
    RiskCalcComplete
    BasketOverall
    CounterpartyId
    FundingRequestCode
    Liquidity
    PortfolioId
    ExecScheme
    TransactionType
    PortfolioReadOnly
    ReconcileStatus
    IsHolidayErrorOrWarning
    BasketInstrumentReportingCurrency
    BasketInstrumentIssue
  }
`

export const RAW_ASSET_DETAILS_DATA_FRAGMENT = gql`
  fragment RawAssetDetailsDataFragment on AssetDetailData {
    AssetType
    UniqueBatchId
    BuyOrSell
    Currency
    Isin
    Country
    Name
    Quantity
    Price
    SecurityTypeCheck
    NotionalExposure
    AvgVol20D
    SinglePosAgainstCpty
    SinglePosAgainstCapMarketFirm
    OutstandingShares
    MarketCapLessThanB
    Overall
    Ticker
    PrimaryExchange
    Notional
    SecurityType
    TraderPrice
    MarketValue
    MaturityDate
    CountryRegion
    Fitch
    Moody
    SAndP
    RiskCalcDividendPriorTerminationPassed
  }
`

export const RAW_ASSET_NOTE_FRAGMENT = gql`
  fragment RawAssetNoteDataFragment on AssetNoteData {
    Note
    CreatedDate
    CreatedBy
    EventName
  }
`

export const FUNDING_REQUEST_OPTION_FRAGMENT = gql`
  fragment FundingRequestOptionFragment on FundingRequestOption {
    FundingRequestId
    FundingRequestCode
    selectedFR
  }
`

export const EXECUTION_SCHEME_OPTION_FRAGMENT = gql`
  fragment ExecutionSchemeOptionFragment on ExecutionSchemeOption {
    OemsExecSchemeId
    ExecutionScheme
    currSelectedExecScheme
  }
`

export const FR_ATTACHED_ASSET_SUM_FRAGMENT = gql`
  fragment FRAttachedAssetSumFragment on AssetSummaryData {
    assetTradeDetails{
      TRSReceiver
      TradeDate
      EffectiveDate
      MaturityDate
      MarketValueNotionalAmount
      TRSFinancingRateType
      TRSFinancingRateVal
      TRSFinancingRateAmountHeader
      TRSFinancingRateAmount
    }
    cpTradeDetails{
      IssuerName
      TradeDate
      SettlementDate
      FaceAmount
      Proceeds  
    }
    assetCashflows {
      TRSIncome
      ADC
      AssetAcquisitionCost
      Total
    }
    liabilityCashflows {
      DebtCost
      FinanceCost
      Total
    }
    overall {
      NetExcess
      RegulatoryCapitalTest
      FRCode
    }
  }
`

export const FR_RECONCILE_SUMMARY_DATA_FRAGMENT = gql`
  fragment FRReconcileSummaryData on ReconcileSummary {
    ReconcileID
    ReconcileNote
    TRSRateInit
    TRSRateAffirm
    trs_rate_mismatch
    TradeDateInit
    TradeDateAffirm
    trade_date_mismatch
    EffectiveDateInit
    EffectiveDateAffirm
    effective_date_mismatch
    MaturityDateInit
    MaturityDateAffirm
    maturity_date_mismatch
    FinalSettlementDateInit
    FinalSettlementDateAffirm
    final_settlement_date_mismatch
    NotionalInit
    NotionalAffirm
    notional_mismatch
  }
`

export const FR_RECONCILE_ASSETD_DETAILS_FRAGMENT = gql`
  fragment FRReconcileAssetDetailsData on ReconcileAssetDetails {
    InstrumentId
    Description
    Cusip
    ISIN
    Country
    Ticker
    QuantityInit
    QuantityAffirm
    SideInit
    SideAffirm
    MarketValueInit
    MarketValueAffirm
    SecurityMasterIdMatch
    quantity_mismatch
    side_mismatch
    security_mismatch
  }
`
